import React from 'react';

import { TOOLBAR_ACTION_SUBJECT_ID as TOOLBAR_BUTTON } from '@atlaskit/editor-common/analytics/types/toolbar-button';

import { useLoomEntryPointErrorCallback } from '@confluence/loom-utils/entry-points/useLoomEntryPointErrorCallback.ts';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant.ts';
import {
	CrossFlowMenuItem,
	CrossJoinMenuItem,
} from '@confluence/loom-utils/entry-points/LoomMenuItem.tsx';
import type {
	EditorAPIRef,
	EditorLoomButtonProps,
} from '@confluence/loom-editor-utils/entry-points/loomEditorUtils-types.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { ButtonComponent } from '@atlaskit/editor-plugin-loom/types';

const ENTRYPOINT_ID = 'loom-confluence-page-editor-toolbar';
const SOURCE_COMPONENT = 'loomConfluencePageEditorToolbar';
const SOURCE_CONTEXT = 'confluencePageEditorToolbarRecordAVideo';

const EditorLoomButtonComponent = ({
	buttonComponent: ButtonComponent,
	flags,
}: EditorLoomButtonProps) => {
	const { entryPointVariant, error, loading } = useLoomEntryPointVariant();

	const loomEntryPointErrorCallback = useLoomEntryPointErrorCallback({ flags });

	if (entryPointVariant === 'BETA' || entryPointVariant === 'CO_USE') {
		// Fall back to the plugin's default behavior when in a recorder-eligible state
		return <ButtonComponent />;
	}

	if (error) {
		return <ButtonComponent onClickBeforeInit={loomEntryPointErrorCallback} />;
	}

	if (loading) {
		return <ButtonComponent isDisabled />;
	}

	if (entryPointVariant === 'CROSS_FLOW') {
		return (
			<CrossFlowMenuItem
				buttonComponent={ButtonComponent}
				sourceActionSubjectId={TOOLBAR_BUTTON.RECORD_VIDEO}
				entryPointId={ENTRYPOINT_ID}
				sourceComponent={SOURCE_COMPONENT}
				sourceContext={SOURCE_CONTEXT}
			/>
		);
	}

	if (entryPointVariant === 'CROSS_JOIN') {
		return (
			<CrossJoinMenuItem
				buttonComponent={ButtonComponent}
				sourceActionSubjectId={TOOLBAR_BUTTON.RECORD_VIDEO}
				entryPointId={ENTRYPOINT_ID}
			/>
		);
	}

	return null;
};

export const EditorLoomButton: React.ComponentType<
	Omit<
		{
			buttonComponent: ButtonComponent;
			editorAPIRef: EditorAPIRef;
		},
		'flags'
	>
> = withFlags(EditorLoomButtonComponent);
